import React, { useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Column, Grid, Button, Stack, Tile } from "@carbon/react";
import { ArrowRight, Filter } from "@carbon/icons-react";
import Selector from "../Selector";
import DateRangeFilter from "../DateRangeFilter";
import FilterMap from "../Map";
import { getEffectiveOptions, isFilterValueUnset, calculateCentroid, applyCascadeToValues, buildMakeMakeValues, serializeDateRange } from "../utils";
import { parseDate, formatDate } from "../../../utils/dateUtils";
import { areFilterValuesEqual } from "../../../utils/filterValueUtils";
import { selectFilterConfig, selectFilterValues, selectCurrentDashboardId, setFilterValues, incrementApplyFiltersVersion } from "../../../features/dashboard/dashboardSlice";
import { useTranslation } from "react-i18next";

const FilterContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filterConfig = useSelector(selectFilterConfig);
  const filterValues = useSelector(selectFilterValues);
  const currentDashboardId = useSelector(selectCurrentDashboardId);
  const filterValuesRef = useRef(filterValues);
  filterValuesRef.current = filterValues;

  const setWithCascade = useCallback((id, newVal) => {
    if (Object.keys(filterValues).length === 0) return;
    const next = applyCascadeToValues(filterValues, id, newVal, filterConfig);
    if (areFilterValuesEqual(filterValues, next)) return;
    dispatch(setFilterValues({ dashboardId: currentDashboardId, values: next }));
  }, [filterValues, filterConfig, dispatch, currentDashboardId]);

  const onGenericChange = useCallback((id) => (val) => {
    setWithCascade(id, Array.isArray(val) ? val : val);
  }, [setWithCascade]);

  const onDateRangeChange = useCallback((id) => {
    return (range) => {
      const serializedRange = serializeDateRange(range, formatDate);
      const next = { ...filterValuesRef.current, [id]: serializedRange };
      if (areFilterValuesEqual(filterValuesRef.current, next)) return;
      dispatch(setFilterValues({
        dashboardId: currentDashboardId,
        values: next
      }));
    };
  }, [dispatch, currentDashboardId]);

  const onMakeMakeChange = useCallback((filter) => (value) => {
    const result = buildMakeMakeValues(filterValues, filter, value, filterConfig);
    if (result.shouldCascade) {
      setWithCascade(result.childId, result.value);
    } else {
      if (areFilterValuesEqual(filterValues, result.values)) return;
      dispatch(setFilterValues({ dashboardId: currentDashboardId, values: result.values }));
    }
  }, [filterValues, filterConfig, setWithCascade, dispatch, currentDashboardId]);

  const handleApplyFilters = useCallback(() => {
    dispatch(incrementApplyFiltersVersion());
  }, [dispatch]);
  const renderFilter = (filter) => {
    if (!filter) return null;
    switch (filter.type) {
      case "makemake_selector":
      case "generic_selector": {
        const parentValue = filter.depends_on?.id ? filterValues[filter.depends_on.id] : undefined;

        if (filter.type === "generic_selector" && filter.depends_on?.id) {
          const parentFilter = { null_label: filter.depends_on?.null_label, allow_null: true };
          if (isFilterValueUnset(parentValue, parentFilter)) return null;
        }

        const effectiveOptions = getEffectiveOptions(filter, parentValue);

        if (filter.type === "makemake_selector") {
          const mmNullLabel = filter.null_label || t("filters.all");
          const currentVal = filterValues[filter.id];
          const mmFilter = { ...filter, null_label: mmNullLabel };
          const isAll = isFilterValueUnset(currentVal, mmFilter);
          const selectorValue = isAll ? mmNullLabel : currentVal;

          return (
            <Selector
              config={{
                ...filter,
                depends_on: undefined,
                label: filter.label,
                description: filter.description,
                selectorType: "single_selector",
                options: effectiveOptions,
                allow_null: filter.allow_null,
                null_label: mmNullLabel,
              }}
              value={selectorValue ?? ""}
              onChange={(selected) => {
                const next = selected === mmNullLabel ? "all" : selected;
                onMakeMakeChange(filter)(next);
              }}
              parentValue={parentValue}
            />
          );
        }

        return (
          <Selector
            config={{
              ...filter,
              options: effectiveOptions,
            }}
            value={filterValues[filter.id] ?? ""}
            onChange={onGenericChange(filter.id)}
            parentValue={parentValue}
          />
        );
      }

      case "date_range":
      case "single_date": {
        const dateValue = filterValues[filter.id];
        const parsedValue = dateValue
          ? (Array.isArray(dateValue)
              ? dateValue.map(parseDate)
              : parseDate(dateValue))
          : undefined;

        return (
          <DateRangeFilter
            config={{
              label: filter.label,
              verbose_name: filter.verbose_name,
              widget_type: filter.type,
            }}
            value={parsedValue}
            onChange={onDateRangeChange(filter.id)}
          />
        );
      }

      default:
        return null;
    }
  };

  const renderMapForFilter = (filter) => {
    if (filter.type !== "makemake_selector") return null;

    const parentPark = filter.depends_on?.id ? filterValues[filter.depends_on.id] : undefined;
    const optionsMap = filter.depends_on?.options_map || {};
    const parentNull = filter.depends_on?.null_label;
    const key = !parentPark || parentPark === parentNull ? (optionsMap[parentNull] ? parentNull : "all") : parentPark;
    const effectiveOptions = Array.isArray(optionsMap[key]) && optionsMap[key].length
      ? optionsMap[key]
      : (filter.options || []);

    return (
      <FilterMap
        key={`${filter.id}-map`}
        mapConfig={{
          ...filter.mapConfig,
          name: parentPark || filter.mapConfig?.name,
          center: calculateCentroid(effectiveOptions, filter.mapConfig?.center),
        }}
        options={effectiveOptions}
        value={filterValues[filter.id] ?? (filter.allow_null ? "all" : "")}
        onChange={onMakeMakeChange(filter)}
        allowNull={filter.allow_null}
        nullLabel={filter.null_label}
      />
    );
  };

  const hasMakeMakeSelector = filterConfig.some(f => f.type === "makemake_selector");
  const mapFilter = filterConfig.find(f => f.type === "makemake_selector");


  return (
    <div className="filters-wrapper">
      <Grid fullWidth className="filters-grid">
        <Column lg={4} md={4} sm={4} className="filters-grid__column">
          <Tile className="filters-list-panel">
            <div className="filters-list-panel__header">
              <Filter size={20} aria-hidden="true" />
              <h2>{t("filters.title")}</h2>
            </div>
            <Stack gap={6}>
              <Stack gap={5} className="filters-list">
                {filterConfig.map((f) => {
                  const filterNode = renderFilter(f);
                  const typeClass = `filters-list__item--${f.type}`;
                  return (
                    <div key={f.id} className={`filters-list__item ${typeClass}`}>
                      {filterNode || <div className="filters-list__empty" />}
                    </div>
                  );
                })}
              </Stack>

              {filterConfig.length > 0 && (
                <Button
                  className="filters-apply-button"
                  kind="primary"
                  size="lg"
                  renderIcon={ArrowRight}
                  onClick={handleApplyFilters}
                >
                  {t("filters.apply")}
                </Button>
              )}
            </Stack>
          </Tile>
        </Column>

        {hasMakeMakeSelector && mapFilter && (
          <Column lg={12} md={4} sm={4} className="filters-grid__column filters-grid__column--map">
            <Tile className="filters-map-panel">
              {renderMapForFilter(mapFilter)}
            </Tile>
          </Column>
        )}
      </Grid>
    </div>
  );
};

export default FilterContainer;
